import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthUserContext, withAuthorization, withEmailVerification } from '../Session';
import * as ROUTES from '../../constants/routes';
import useHomeData from './hooks/useHomeData';
import HomeHeader from './HomeHeader';

import OverviewTab from './OverviewTab';
import ActivityTab from './ActivityTab';
import UsersTab from './Admin/UsersTab';
import SystemTab from './Admin/SystemTab';
import '../Admin/admin.css';
import './styles.css';

const TABS_CONFIG = (t, isOrgAdmin, activityLogs, orgUsers) => {
  const list = [
    { id: 'overview', label: t('admin.tab_overview'), icon: '🏠', count: null },
    { id: 'activity', label: t('admin.tab_activity'), icon: '📈', count: activityLogs.length },
  ];
  if (isOrgAdmin) {
    list.push({ id: 'users', label: t('admin.tab_users'), icon: '👥', count: orgUsers.length });
    list.push({ id: 'system', label: t('admin.tab_settings'), icon: '⚙️', count: null });
  }
  return list;
};

const HomePage = () => {
  const { t } = useTranslation();
  const authUser = useContext(AuthUserContext);

  const {
    projects, projectsLoading,
    selectedProject, selectProject, selectedProjectDate,
    tasks, tasksLoading, taskStats,
    activityLogs, projectActivityLogs, activitySearch, setActivitySearch,
    activityFilter, setActivityFilter, actionOptions,
    orgUsers,
    serverHealth, documents, shapeCountsByOrg,
    refreshHealth,
    organizations, currentOrg, switchOrg,
    isOrgAdmin,
    activeTab, setActiveTab,
    assemblyData, assemblyLoading,
    annotatedPages, annotatedLoading,
    purchaseOrders, poLoading,
  } = useHomeData();

  const displayName = authUser?.username || authUser?.email || '';

  const tasksDueSoon = useMemo(() => {
    return tasks.filter(t => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
      const weekFromNow = new Date();
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      return due <= weekFromNow && due >= new Date();
    }).length;
  }, [tasks]);

  const tabs = useMemo(
    () => TABS_CONFIG(t, isOrgAdmin, activityLogs, orgUsers),
    [t, isOrgAdmin, activityLogs, orgUsers]
  );

  return (
    <div className="main">
      <div className="admin-container">

        <HomeHeader
          displayName={displayName}
          organizations={organizations}
          currentOrg={currentOrg}
          onSwitchOrg={switchOrg}
          isOrgAdmin={isOrgAdmin}
          projectsCount={projects.length}
          tasksInProgress={taskStats.inProgress}
          tasksDueSoon={tasksDueSoon}
        />

        <nav className="admin-tabs" style={{display : 'none' }}> /* not ready for production */
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.count !== null && <span className="tab-badge">{tab.count}</span>}
            </button>
          ))}
        </nav>

        <section className="admin-content">
          {activeTab === 'overview' && (
            <OverviewTab
              displayName={displayName}
              projects={projects}
              projectsLoading={projectsLoading}
              selectedProject={selectedProject}
              selectProject={selectProject}
              selectedProjectDate={selectedProjectDate}
              tasks={tasks}
              tasksLoading={tasksLoading}
              taskStats={taskStats}
              activityLogs={activityLogs}
              projectActivityLogs={projectActivityLogs}
              currentOrg={currentOrg}
              assemblyData={assemblyData}
              assemblyLoading={assemblyLoading}
              annotatedPages={annotatedPages}
              annotatedLoading={annotatedLoading}
              purchaseOrders={purchaseOrders}
              poLoading={poLoading}
              isOrgAdmin={isOrgAdmin}
            />
          )}

          {activeTab === 'activity' && (
            <ActivityTab
              activityLogs={activityLogs}
              activitySearch={activitySearch}
              onSearchChange={setActivitySearch}
              activityFilter={activityFilter}
              onFilterChange={setActivityFilter}
              actionOptions={actionOptions}
            />
          )}

          {activeTab === 'users' && isOrgAdmin && (
            <UsersTab orgUsers={orgUsers} currentOrg={currentOrg} />
          )}

          {activeTab === 'system' && isOrgAdmin && (
            <SystemTab
              serverHealth={serverHealth}
              projects={projects}
              shapeCountsByOrg={shapeCountsByOrg}
              orgUsers={orgUsers}
              documents={documents}
              onRefreshHealth={refreshHealth}
            />
          )}
        </section>
      </div>
    </div>
  );
};

const hasOrganization = authUser =>
  !!authUser &&
  !!authUser.roles &&
  Object.keys(authUser.roles).some(role => authUser.roles[role] !== null);

const condition = authUser => hasOrganization(authUser);
export default withEmailVerification(
  withAuthorization(condition, ROUTES.ACCOUNT)(HomePage)
);
